import { classNames, IconName, ICONS, useWindowDimensions } from '@fl/cmsch-fe-library';
import { ItemType } from 'antd/es/menu/hooks/useItems';
import { startsWith } from 'fp-ts/string';
import { isUndefined } from 'lodash/fp';
import React, { createRef, Fragment, FC, memo, ReactNode, RefObject, useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router';
import { opt, optEmptyArray } from 'ts-opt';
import { Ant } from 'common/ant';
import { MenuItem } from 'common/layout/types/menu-item';
import styles from './styles.sass';
const spaceBeforeActionIcons = 50;
interface Props {
  /** Menu item metadata that drives label, path, and submenu rendering. */
  menuItem: MenuItem;
  /** Current pathname used for active-state detection. */
  locationPath: string;
  /** Whether the item is currently visible after overflow balancing. */
  visible: boolean;
  /** Ref to the action icons container used when balancing overflow. */
  actionIconsRef: RefObject<HTMLDivElement>;
  /** Moves items in or out of the overflow menu based on available space. */
  toggleOverflowedMenuItem(key: string): void;
}

/** Renders a menu label with the optional icon used by the shell navigation. */
export const getLabel = (label: string, isSubMenu: boolean, icon?: IconName): ReactNode => <Fragment>
        <div className={isSubMenu ? styles.subMenuLabel : styles.menuLabel}>
            {icon && ICONS[icon]}
            {label}
        </div>
    </Fragment>;

/** Checks whether the current path should mark the menu item or submenu as active. */
export const isActive = (locationPath: string, path?: string, subMenuItems?: Array<MenuItem>, pathsNotInMenu?: Array<string>): boolean => path === locationPath || (subMenuItems || []).some(x => {
  const isActiveMenuItem = isActive(locationPath, path, x.subMenuItems, x.pathsNotInMenu);
  return x.exactPath ? x.path === locationPath || isActiveMenuItem : x.path && locationPath.startsWith(x.path) || isActiveMenuItem;
}) || (pathsNotInMenu || []).some(startsWith(locationPath));

/** Converts navigation items into Ant Design submenu items for dropdown rendering. */
export const buildSubMenuItems = (menuItems: Array<MenuItem>, locationPath: string): Array<ItemType> => menuItems.map(x => optEmptyArray(x.subMenuItems).caseOf<ItemType>(y => ({
  key: x.key,
  className: classNames(isActive(locationPath, x.path, x.subMenuItems) && styles.active, styles.item),
  label: getLabel(x.label, true, x.icon),
  children: buildSubMenuItems(y, locationPath)
}), () => x.path ? {
  key: x.key,
  className: classNames(isActive(locationPath, x.path, x.subMenuItems) && styles.active),
  label: !x.isExternalLink ? <Link to={x.path} target={x.openIn} data-test-id={x.key}>
                        {getLabel(x.label, true, x.icon)}
                    </Link> : <a href={x.path} target={x.openIn} rel="noreferrer" data-test-id={x.key}>
                        {getLabel(x.label, true, x.icon)}
                    </a>
} : null));
interface ButtonProps {
  /** Menu key used for the rendered element and test id. */
  menuKey: string;
  /** Target path for the main click area. */
  path: string | undefined;
  /** Whether the item should render as an external anchor. */
  isExternalLink?: boolean;
  /** Optional target used for menu links. */
  openIn?: '_blank';
  /** Ref used to measure overflow and menu placement. */
  reference: RefObject<HTMLDivElement>;
  /** Already formatted label node rendered in the button. */
  menuItemLabel: ReactNode;
  /** CSS class string applied to the button wrapper. */
  classes: string;
  /** Child submenu items used to determine the first navigation target. */
  subMenuItems?: Array<MenuItem>;
}

// for some reasons it can't be as FunctionComponent (dropdown menu on hover doesn't work)
const button = ({
  menuKey,
  path,
  menuItemLabel,
  reference,
  classes,
  subMenuItems,
  isExternalLink,
  openIn
}: ButtonProps): ReactNode => {
  const firstSubMenuItemPath = opt(subMenuItems).headIn().prop('path').orUndef();
  return <div className={classes} key={menuKey} ref={reference} data-test-id={menuKey} data-sentry-component="button" data-sentry-source-file="main-menu-item.tsx">

            {path && !isExternalLink ? <Link to={firstSubMenuItemPath ?? path} target={openIn}>
                        {menuItemLabel}
                    </Link> : <a href={path || ''} target={openIn} rel="noreferrer">
                        {menuItemLabel}
                    </a>}
        </div>;
};
const MainMenuItemBase: FC<Props> = props => {
  const {
    menuItem,
    locationPath,
    visible,
    actionIconsRef,
    toggleOverflowedMenuItem
  } = props;
  const {
    label,
    key,
    path,
    icon,
    subMenuItems,
    pathsNotInMenu,
    isExternalLink,
    openIn
  } = menuItem;
  const ref = createRef<HTMLDivElement>();
  const {
    width,
    height
  } = useWindowDimensions();
  const active = useMemo(() => isActive(locationPath, path, subMenuItems, pathsNotInMenu), [locationPath, path, pathsNotInMenu, subMenuItems]);
  const menuItemLabel: ReactNode = getLabel(label, false, icon);
  const className = classNames(styles.menuItem, active && styles.active, !visible && 'invisible');
  useEffect(() => {
    const {
      width: actionIconsWidth
    } = actionIconsRef.current?.getBoundingClientRect() || {};
    const ourWidth = width - (actionIconsWidth || 0) - spaceBeforeActionIcons;
    const {
      right
    } = ref.current?.getBoundingClientRect() || {};
    if (!isUndefined(right) && (right < ourWidth && !visible || right > ourWidth && visible)) {
      toggleOverflowedMenuItem(key);
    }
  }, [height, label, ref, visible, toggleOverflowedMenuItem, key, actionIconsRef, width]);
  const [openKeys, setOpenKeys] = useState<Array<string>>([]);
  const onOpenChange = useCallback((keys: Array<string>) => {
    const latest = keys.find(k => !openKeys.includes(k));
    if (latest) {
      setOpenKeys([latest]);
    } else {
      setOpenKeys(keys);
    }
  }, [openKeys]);
  return optEmptyArray(subMenuItems).caseOf(sbItems => <Ant.Dropdown overlay={<Ant.Menu openKeys={openKeys} onOpenChange={onOpenChange} items={buildSubMenuItems(sbItems, locationPath)} />} placement="bottomLeft" arrow overlayClassName={styles.dropdown}>
                {button({
      menuKey: key,
      classes: className,
      path,
      menuItemLabel,
      reference: ref,
      subMenuItems: sbItems,
      isExternalLink,
      openIn
    })}
            </Ant.Dropdown>, () => <Fragment>
                {button({
      menuKey: key,
      classes: className,
      path,
      menuItemLabel,
      reference: ref,
      isExternalLink,
      openIn
    })}
            </Fragment>);
};

/**
 * Renders one responsive menu item and switches between direct links and dropdown menus.
 *
 * @param props Menu item metadata, current location, and overflow state.
 * @returns A menu item rendered as either a direct link or a dropdown trigger.
 *
 * @example
 * <MainMenuItem
 *     menuItem={menuItem}
 *     locationPath="/home"
 *     visible
 *     actionIconsRef={actionIconsRef}
 *     toggleOverflowedMenuItem={toggleOverflowedMenuItem}
 * />
 */
export const MainMenuItem = memo(MainMenuItemBase);